/**
 * Translator between the old `Shul` shape (what every shul component,
 * resolveSchedule() and Next Minyan use) and the new `ShulListing` shape
 * stored in /listings. See docs/phase-1-plan.md "Approach: a translator".
 *
 * Keep legacyShulToListing() in sync with scripts/lib/listingConvert.js
 * (the copy script's version); a test checks both give the same result.
 */
import type { Contact, ListingStatus, Shul, ShulListing } from '../types';
import { DEFAULT_AREA_ID } from '../config/areas';
import { LISTING_SCHEMA_VERSION } from '../config/categories';

export { LISTING_SCHEMA_VERSION };

/** The id given to the contact created from a shul's phone number. */
export const SHUL_PHONE_CONTACT_ID = 'contact-1';

/** Drops keys whose value is undefined (Firestore rejects undefined). */
function withoutUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as T;
}

function trimmedOrUndefined(value: string | undefined): string | undefined {
  const s = value?.trim();
  return s ? s : undefined;
}

/** Old shul flags → new status. */
export function shulFlagsToStatus(
  shul: Pick<Shul, 'active' | 'pendingApproval'>
): ListingStatus {
  if (shul.pendingApproval === true) return 'pending';
  if (shul.active === true) return 'published';
  return 'hidden';
}

/** Listing in /listings → the old Shul shape the shul components expect. */
export function listingToShul(listing: ShulListing): Shul {
  const phone = listing.contacts?.[0]?.phone;
  return withoutUndefined({
    id: listing.id,
    nameEn: listing.nameEn ?? '',
    nameHe: listing.nameHe ?? '',
    address: listing.location?.addressEn || listing.location?.addressHe || '',
    // 0 = "no coordinates": shul components already hide the map when lat/lng are falsy.
    lat: listing.location?.lat ?? 0,
    lng: listing.location?.lng ?? 0,
    phone: phone || undefined,
    description: listing.descriptionEn,
    descriptionHe: listing.descriptionHe,
    photoUrl: listing.photoUrl,
    posters: listing.posters,
    nusach: listing.nusach,
    schedules: listing.schedules ?? [],
    editorUids: listing.ownerUids ?? [],
    active: listing.status === 'published',
    pendingApproval: listing.status === 'pending',
    createdAt: listing.createdAt,
    noteEn: listing.noteEn || undefined,
    noteHe: listing.noteHe || undefined,
    messages: listing.messages?.length ? listing.messages : undefined,
  }) as Shul;
}

/**
 * A whole old-shape shul → a new listing document (without id).
 * Used for new shuls (super admin "add shul", self-signup) and mirrors
 * the copy script's conversion of existing /shuls documents.
 */
export function legacyShulToListing(
  shul: Omit<Shul, 'id'>,
  nowIso: string
): Omit<ShulListing, 'id'> {
  const phone = trimmedOrUndefined(shul.phone);
  const contacts: Contact[] = phone ? [{ id: SHUL_PHONE_CONTACT_ID, phone }] : [];
  const hasCoords = typeof shul.lat === 'number' && typeof shul.lng === 'number' && !!shul.lat && !!shul.lng;

  return withoutUndefined({
    schemaVersion: LISTING_SCHEMA_VERSION,
    category: 'shul' as const,
    nameEn: shul.nameEn ?? '',
    nameHe: shul.nameHe ?? '',
    descriptionEn: trimmedOrUndefined(shul.description),
    descriptionHe: trimmedOrUndefined(shul.descriptionHe),
    messages: [],
    location: withoutUndefined({
      // Today's shul addresses are English transliterations ("Chagual 5").
      addressEn: shul.address ?? '',
      addressHe: '',
      lat: hasCoords ? shul.lat : undefined,
      lng: hasCoords ? shul.lng : undefined,
      areaId: DEFAULT_AREA_ID,
      showExactLocation: true,
    }),
    contacts,
    hours: [],
    hoursMode: 'scheduled' as const,
    details: {},
    schedules: shul.schedules ?? [],
    nusach: shul.nusach ?? 'ashkenaz',
    posters: shul.posters,
    photoUrl: shul.photoUrl,
    tags: [],
    ownerUids: shul.editorUids ?? [],
    status: shulFlagsToStatus(shul),
    createdAt: shul.createdAt || nowIso,
    updatedAt: nowIso,
  }) as Omit<ShulListing, 'id'>;
}

/** What the patch translator needs to know about the stored listing. */
export interface ListingPatchContext {
  /** Current contacts, so a phone change keeps the other contacts. */
  contacts?: Contact[];
  /** Current status, so toggling `active` on a pending listing keeps it pending. */
  status?: ListingStatus;
}

/** Fields of Shul that map 1:1 onto the listing. */
const SAME_NAME_FIELDS = [
  'nameEn',
  'nameHe',
  'descriptionHe',
  'photoUrl',
  'posters',
  'nusach',
  'schedules',
  'createdAt',
] as const;

/**
 * A partial update in the old Shul shape → a Firestore update for the listing
 * (dotted paths such as `location.addressEn`). Undefined values are ignored,
 * like the old hooks did. `id` is never written.
 */
export function shulPatchToListingPatch(
  patch: Partial<Shul>,
  context: ListingPatchContext = {}
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const key of SAME_NAME_FIELDS) {
    if (patch[key] !== undefined) out[key] = patch[key];
  }
  if (patch.description !== undefined) out.descriptionEn = patch.description;
  if (patch.address !== undefined) out['location.addressEn'] = patch.address;
  if (patch.lat !== undefined) out['location.lat'] = patch.lat;
  if (patch.lng !== undefined) out['location.lng'] = patch.lng;
  if (patch.editorUids !== undefined) out.ownerUids = patch.editorUids;

  if (patch.phone !== undefined) {
    const current = context.contacts ?? [];
    const [first, ...rest] = current;
    const phone = patch.phone.trim();
    const updatedFirst: Contact = withoutUndefined({
      ...(first ?? { id: SHUL_PHONE_CONTACT_ID }),
      phone: phone || undefined,
    });
    const firstHasContent = Object.keys(updatedFirst).some(
      (k) => k !== 'id' && updatedFirst[k as keyof Contact]
    );
    out.contacts = firstHasContent ? [updatedFirst, ...rest] : rest;
  }

  if (patch.pendingApproval === true) {
    out.status = 'pending';
  } else if (patch.active !== undefined) {
    const stayPending = context.status === 'pending' && patch.pendingApproval !== false;
    if (!stayPending) out.status = patch.active ? 'published' : 'hidden';
  }

  return out;
}
